// Analytics — PostHog wrapper.
//
// posthog-js is lazy-loaded via `src/lib/posthog-lazy.ts` so the SDK (~61 KB gz)
// doesn't ship in the entry chunk. Marketing visitors who never consent never
// download it. The legacy synchronous API (`track`, `identify`, `reset`) is
// preserved by queueing calls until the SDK finishes loading.

import { ensurePostHog, getPostHog, queuePostHogCall } from './posthog-lazy';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';

// Routes that should never be recorded (replay session is stopped on entry).
// PostHog still loads + tracks events on these pages — only the DOM replay is
// suppressed. /billing exposes card brand + Stripe portal handoffs; /admin
// exposes other tenants' data. Both are out of scope for product-UX replay.
export const REPLAY_BLOCKED_ROUTES = ['/billing', '/admin'];

export function isReplayBlockedPath(path: string): boolean {
  return REPLAY_BLOCKED_ROUTES.some((p) => path === p || path.startsWith(`${p}/`));
}

let _initialised = false;

/**
 * Bootstrap PostHog after consent has been granted. Triggers the dynamic
 * import of posthog-js, then calls `posthog.init()`. Returns a promise so
 * callers can `await` for tests / determinism; production callers fire and
 * forget.
 */
export async function initAnalytics(): Promise<void> {
  if (!POSTHOG_KEY) return;
  if (_initialised) return;
  _initialised = true;
  try {
    const posthog = await ensurePostHog();
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      // 100% session-replay sampling — solo founder onboarding cohort, MAX
      // visibility. Privacy: all inputs masked by default; [data-private]
      // elements have their text masked; .private elements are blocked entirely.
      // Sensitive routes (/billing, /admin) skip recording via the `loaded`
      // hook below.
      session_recording: {
        recordCrossOriginIframes: false,
        maskAllInputs: true,
        maskTextSelector: '[data-private]',
        blockSelector: '.private',
      },
      disable_session_recording: false,
      enable_recording_console_log: true,
      loaded: (ph) => {
        if (typeof window === 'undefined') return;
        const evaluate = () => {
          try {
            if (isReplayBlockedPath(window.location.pathname)) {
              ph.stopSessionRecording();
            } else {
              ph.startSessionRecording();
            }
          } catch {
            // Test mocks may not expose start/stop — safe to ignore.
          }
        };
        evaluate();
        window.addEventListener('popstate', evaluate);
        // SPA route changes go through history.pushState — patch it once so we
        // re-evaluate on every navigation. Idempotent: only patches if not
        // already wrapped.
        const origPush = window.history.pushState;
        type PushStateFn = typeof window.history.pushState;
        type WrappedPush = PushStateFn & { __riq_replay_patched?: boolean };
        if (!(origPush as WrappedPush).__riq_replay_patched) {
          const wrapped: WrappedPush = function (
            this: History,
            ...args: Parameters<PushStateFn>
          ) {
            const r = origPush.apply(this, args);
            evaluate();
            return r;
          };
          wrapped.__riq_replay_patched = true;
          window.history.pushState = wrapped;
        }
      },
    });
  } catch {
    // Dynamic import or init failed — analytics is best-effort. Allow a retry
    // on the next call by clearing the guard.
    _initialised = false;
  }
}

/**
 * Ensure PostHog has been kicked off when consent + key are present. Called
 * implicitly by `track` / `identify` / `reset` so callers don't need to
 * remember — the cost is a single dynamic import per page load.
 */
function ensureLoadedIfKey() {
  if (!POSTHOG_KEY) return;
  if (getPostHog()) return;
  // Fire and forget — the queue inside posthog-lazy.ts catches calls until the
  // SDK finishes loading and then replays them.
  void ensurePostHog().catch(() => { /* ignore */ });
}

export function identify(userId: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  const ph = getPostHog();
  if (ph) {
    ph.identify(userId, properties);
  } else {
    queuePostHogCall({ kind: 'identify', userId, properties });
    ensureLoadedIfKey();
  }
}

export function track(event: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  const ph = getPostHog();
  if (ph) {
    ph.capture(event, properties);
  } else {
    queuePostHogCall({ kind: 'capture', event, properties });
    ensureLoadedIfKey();
  }
}

export function reset() {
  clearOnceFlags();
  if (!POSTHOG_KEY) return;
  const ph = getPostHog();
  if (ph) {
    ph.reset();
  } else {
    queuePostHogCall({ kind: 'reset' });
    ensureLoadedIfKey();
  }
}

// Funnel-level events. Keep this list small and stable — these are the metrics
// we'll instrument dashboards on. Add new events only when you have a clear
// reason to track them (don't fire-hose PostHog with noise).
export const Events = {
  // Auth
  SIGN_IN: 'sign_in',
  SIGN_UP: 'sign_up',
  SIGN_OUT: 'sign_out',
  // Studio
  STUDIO_UPLOAD: 'studio_upload',
  STUDIO_TRANSFORM_START: 'studio_transform_start',
  STUDIO_TRANSFORM_SUCCESS: 'studio_transform_success',
  STUDIO_TRANSFORM_FAIL: 'studio_transform_fail',
  STUDIO_EXPORT: 'studio_export',
  // Listings
  LISTING_CREATE: 'listing_create',
  LISTING_VIEW: 'listing_view',
  // North Star metric — WALP (Weekly Active Listings Published) is driven by
  // this event. Fires on successful Single Property Website publish. See
  // docs/operations/north-star.md.
  LISTING_PUBLISH: 'listing_publish',
  // CRM
  CONTACT_ADDED: 'contact_added',
  // Showings
  SHOWING_BOOKED: 'showing_booked',
  // Property website
  INQUIRY_SUBMITTED: 'inquiry_submitted',
  // Marketing
  MARKETING_GENERATE: 'marketing_generate',
  // Billing
  BILLING_PAGE_VIEWED: 'billing_page_viewed',
  UPGRADE_CLICKED: 'upgrade_clicked',
  SUBSCRIPTION_STARTED: 'subscription_started',
  CHECKOUT_START: 'checkout_start',
  CHECKOUT_SUCCESS: 'checkout_success',
  PORTAL_OPEN: 'billing_portal_open',
  // Errors
  ERROR_BOUNDARY: 'error_boundary_caught',
  UNHANDLED_REJECTION: 'unhandled_rejection',
  // Activation-funnel events (see docs/analytics/posthog-dashboards.md)
  FIRST_ACTION: 'first_action',
  VALUE_MOMENT: 'value_moment',
  HABIT_3_IN_7D: 'habit_3_in_7d',
  PAYWALL_HIT: 'paywall_hit',
  PAID_CONVERSION: 'paid_conversion',
  UPGRADE: 'upgrade',
  DOWNGRADE: 'downgrade',
  CANCEL: 'cancel',
  CREDIT_CHARGED: 'credit_charged',
  FEATURE_USED: 'feature_used',
  ERROR_SHOWN: 'error_shown',
  // Aha-moment funnel events (see docs/posthog-tester-funnel.md). These fill the
  // gaps flagged in PR #729 — `iq_score_first_view` is the canonical aha event,
  // `tour_started` / `tour_completed` are emitted by `<TourOverlay>` for the
  // first-run guided tour, and `dashboard_first_view` is the once-per-user
  // variant of the existing per-mount `feature_used { feature: 'dashboard' }`.
  IQ_SCORE_FIRST_VIEW: 'iq_score_first_view',
  TOUR_STARTED: 'tour_started',
  TOUR_COMPLETED: 'tour_completed',
  DASHBOARD_FIRST_VIEW: 'dashboard_first_view',
} as const;

// localStorage namespace for "fire this event once per user" idempotency.
// Cleared on `reset()` so each sign-out resets the flags for the next user.
const ONCE_FLAG_PREFIX = 'rqio_evt_once_';

function onceKey(eventName: string, scope?: string): string {
  return scope
    ? `${ONCE_FLAG_PREFIX}${eventName}__${scope}`
    : `${ONCE_FLAG_PREFIX}${eventName}`;
}

/**
 * Fires `event` only the first time per browser / user. Subsequent calls
 * become no-ops. Used for once-per-user funnel events: `sign_up`,
 * `first_action`, `value_moment`, `habit_3_in_7d`.
 *
 * Scope makes the flag uid-aware so signing in as a different user resets
 * idempotency naturally — pass the Firebase uid as `scope`.
 */
export function trackOnce(
  event: string,
  scope?: string,
  properties?: Record<string, unknown>,
): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const key = onceKey(event, scope);
    if (window.localStorage.getItem(key)) return false;
    window.localStorage.setItem(key, '1');
    track(event, properties);
    return true;
  } catch {
    // localStorage blocked (Safari private / SSR). Fall through — fire normally.
    track(event, properties);
    return true;
  }
}

/**
 * Clears all per-event once-flags. Called from `reset()` so a sign-out leaves
 * the next user free to re-fire `sign_up` / `first_action`.
 */
function clearOnceFlags() {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(ONCE_FLAG_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

/**
 * Hash a stack trace to an 8-char prefix of its sha256 (browser SubtleCrypto
 * is async — we want sync here, so use a small djb2 + base36 fallback that's
 * good enough for grouping). Cardinality drops from "infinite" (every build's
 * chunk-hashed line numbers rotate) to ~16M slots. Joining on the hash in
 * PostHog still groups same-shape errors across builds.
 */
export function hashStack(stack: string | undefined): string {
  if (!stack) return '';
  let h = 5381;
  for (let i = 0; i < stack.length; i += 1) {
    h = ((h << 5) + h + stack.charCodeAt(i)) | 0;
  }
  // 8-char unsigned-int base36 — keeps property values short + grep-able.
  return (h >>> 0).toString(36).padStart(8, '0').slice(0, 8);
}

/**
 * Top of stack: function name + source line is enough to triage in a single
 * pane. Limits to 200 chars. Does not strip chunk hashes — the hash itself
 * is already captured separately by hashStack().
 */
export function sanitizeStackTop(stack: string | undefined): string {
  if (!stack) return '';
  return stack.split('\n').slice(0, 3).join('\n').slice(0, 200);
}

/**
 * Strip line:column numbers from component stack so the cardinality is bounded
 * by the set of component names rather than build-dependent positions.
 * Caps at 500 chars (was 2000) — component-name lists rarely need more.
 */
export function sanitizeComponentStack(stack: string | undefined): string {
  if (!stack) return '';
  return stack.replace(/:\d+:\d+/g, '').slice(0, 500);
}

/**
 * Strip the Vite chunk-hash portion of an asset filename so window_error
 * events don't fan out cardinality every build. `/assets/index-AbC123.js`
 * becomes `/assets/index-HASH.js`.
 */
function sanitizeFilename(filename: string | undefined): string {
  if (!filename) return '';
  return filename.replace(/-[A-Za-z0-9_]{8,}\.(js|css|mjs)/g, '-HASH.$1');
}

function startReplayIfAllowed() {
  if (typeof window === 'undefined') return;
  if (isReplayBlockedPath(window.location.pathname)) return;
  const ph = getPostHog();
  if (ph) {
    try { ph.startSessionRecording(); } catch { /* ignore */ }
  } else {
    queuePostHogCall({ kind: 'startSessionRecording' });
    ensureLoadedIfKey();
  }
}

// Capture unhandled errors so we get production observability without Sentry.
// Call once at app boot from main.tsx.
export function initErrorTelemetry() {
  if (typeof window === 'undefined') return;
  window.addEventListener('unhandledrejection', (e) => {
    try {
      startReplayIfAllowed();  // 100% capture on error events (except blocked routes)
      const reason = e.reason as { message?: unknown } | undefined;
      track(Events.UNHANDLED_REJECTION, {
        reason: String(reason?.message ?? reason ?? '').slice(0, 500),
      });
    } catch {}
  });
  window.addEventListener('error', (e) => {
    try {
      startReplayIfAllowed();  // 100% capture on error events (except blocked routes)
      track('window_error', {
        message: e.message.slice(0, 300),
        filename: sanitizeFilename(e.filename),
      });
    } catch {}
  });
}

// ---------- Activation-funnel helpers ----------
// Thin, opinionated wrappers over `track()` / `trackOnce()` so callers don't
// repeat property shape across pages. Every helper here matches the taxonomy
// in `docs/analytics/posthog-dashboards.md`.

const ACQUISITION_SCOPE = 'rqio_acquisition_source';

/**
 * Returns a stable acquisition-source descriptor for the current browser.
 * Reads UTM-style hints from the URL on first call and stickies them to
 * localStorage so subsequent sessions still know how the user arrived.
 *
 * No PII — only the source tag and (when present) a referral code.
 */
export function getAcquisitionSource(): {
  source: 'organic' | 'referral' | 'affiliate';
  referralCode?: string;
} {
  if (typeof window === 'undefined') return { source: 'organic' };
  try {
    const cached = window.localStorage.getItem(ACQUISITION_SCOPE);
    if (cached) return JSON.parse(cached);
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || params.get('referral');
    const aff = params.get('aff') || params.get('affiliate');
    let resolved: { source: 'organic' | 'referral' | 'affiliate'; referralCode?: string };
    if (aff) resolved = { source: 'affiliate', referralCode: aff };
    else if (ref) resolved = { source: 'referral', referralCode: ref };
    else resolved = { source: 'organic' };
    window.localStorage.setItem(ACQUISITION_SCOPE, JSON.stringify(resolved));
    return resolved;
  } catch {
    return { source: 'organic' };
  }
}

/**
 * Fires `sign_up` exactly once per uid the first time we see a freshly-signed
 * up user. Safe to call from page mounts — second / third call is a no-op.
 */
export function trackSignUp(uid: string): boolean {
  if (!uid) return false;
  const acq = getAcquisitionSource();
  return trackOnce(Events.SIGN_UP, uid, acq);
}

/**
 * Fires `first_action` exactly once per uid the first time the user completes
 * any meaningful first action (upload, add listing, send message). Subsequent
 * calls are no-ops.
 */
export type FirstActionType =
  | 'upload_photo'
  | 'add_listing'
  | 'send_message'
  | 'generate_marketing';

export function trackFirstAction(
  uid: string,
  action: FirstActionType,
  context?: { planId?: string; daysSinceSignup?: number },
): boolean {
  if (!uid) return false;
  return trackOnce(Events.FIRST_ACTION, uid, {
    action_type: action,
    ...(context?.planId && { plan_id: context.planId }),
    ...(context?.daysSinceSignup !== undefined && { days_since_signup: context.daysSinceSignup }),
  });
}

/**
 * Fires `value_moment` exactly once per uid the first time an AI enhance /
 * generation completes successfully. Carries the output category + credit
 * cost so the dashboard can break down by surface.
 */
export function trackValueMoment(
  uid: string,
  outputType: string,
  iqCreditsSpent: number,
  context?: { planId?: string; featureSurface?: string; daysSinceSignup?: number },
): boolean {
  if (!uid) return false;
  return trackOnce(Events.VALUE_MOMENT, uid, {
    output_type: outputType,
    iq_credits_spent: iqCreditsSpent,
    ...(context?.planId && { plan_id: context.planId }),
    ...(context?.featureSurface && { feature_surface: context.featureSurface }),
    ...(context?.daysSinceSignup !== undefined && { days_since_signup: context.daysSinceSignup }),
  });
}

/**
 * Fires `habit_3_in_7d` exactly once per uid. Called by the client after the
 * server-side `habitFormed` flag flips true on `users/{uid}`. Safe to call
 * on every app boot — only fires once per user.
 */
export function trackHabitFormed(uid: string): boolean {
  if (!uid) return false;
  return trackOnce(Events.HABIT_3_IN_7D, uid);
}

/**
 * Fires `paywall_hit` every time a credit-locked action shows the upgrade
 * modal / toast. Not idempotent — every encounter is signal.
 */
export function trackPaywallHit(
  actionBlocked: string,
  creditsRemaining: number,
  context?: { planId?: string; featureSurface?: string },
) {
  track(Events.PAYWALL_HIT, {
    action_blocked: actionBlocked,
    credits_remaining: creditsRemaining,
    ...(context?.planId && { plan_id: context.planId }),
    ...(context?.featureSurface && { feature_surface: context.featureSurface }),
  });
}

/**
 * Fires `credit_charged` after the server confirms a deduction succeeded.
 * Category maps to the IQ Credits taxonomy (utility / production / intelligence).
 */
export type CreditCategory = 'utility' | 'production' | 'intelligence';

export function trackCreditCharged(
  actionSku: string,
  creditAmount: number,
  creditCategory: CreditCategory,
  context?: { planId?: string; featureSurface?: string },
) {
  track(Events.CREDIT_CHARGED, {
    action_sku: actionSku,
    credit_amount: creditAmount,
    credit_category: creditCategory,
    ...(context?.planId && { plan_id: context.planId }),
    ...(context?.featureSurface && { feature_surface: context.featureSurface }),
  });
}

/**
 * Fires `feature_used` once per page mount. Used inside `useEffect` on each
 * top-level feature page to populate the feature-engagement dashboard.
 *
 * Not idempotent across mounts — that's intentional. We want to count
 * distinct sessions, not unique users.
 */
export function trackFeatureUsed(
  feature: string,
  context?: { planId?: string; featureSurface?: string; daysSinceSignup?: number },
) {
  track(Events.FEATURE_USED, {
    feature,
    ...(context?.planId && { plan_id: context.planId }),
    ...(context?.featureSurface && { feature_surface: context.featureSurface }),
    ...(context?.daysSinceSignup !== undefined && { days_since_signup: context.daysSinceSignup }),
  });
}

/**
 * Fires `error_shown` when a visible error toast / banner is rendered.
 * Message is truncated to 300 chars and stripped of obvious PII patterns
 * before transport.
 */
export function trackErrorShown(
  errorType: string,
  errorMessage?: string,
  context?: { featureSurface?: string },
) {
  track(Events.ERROR_SHOWN, {
    error_type: errorType,
    error_message: sanitizeErrorMessage(errorMessage),
    ...(context?.featureSurface && { feature_surface: context.featureSurface }),
  });
}

function sanitizeErrorMessage(msg: string | undefined): string | undefined {
  if (!msg) return undefined;
  // Strip obvious PII: emails, anything that looks like a Bearer token.
  return msg
    .replace(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g, '[email]')
    .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [token]')
    .slice(0, 300);
}

/** Test-only — reset the init guard. */
export function __resetAnalyticsForTests(): void {
  _initialised = false;
}
